import { ACTION_ICONS, Admin_Code } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import DContext from "../../context/DContext";
import { banksService } from "../../services/banks.service";
import { suppliersService } from "../../services/suppliers.service";
import { rbacRolesService } from "../../services/rbac-roles.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const toArray = (resData) =>
  Array.isArray(resData) ? resData : resData?.data || [];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_COLORS = {
  pending: { bg: "#fff4e6", color: "#e8590c" },
  verified: { bg: "#d4edda", color: "#155724" },
  rejected: { bg: "#f8d7da", color: "#721c24" },
};

const ACCOUNT_TYPE_OPTIONS = [
  { value: "savings", label: "Savings" },
  { value: "current", label: "Current" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const statusLabel = (status) =>
  STATUS_OPTIONS.find((o) => o.value === status)?.label || status || "-";

const BankDetails = () => {
  const { start, complete } = useLoadingBar();
  const { authState } = useContext(DContext);
  const { user } = authState;
  const isSupplierUser = String(authState?.user_role || "")
    .toLowerCase()
    .includes("supplier");

  const [loading, setLoading] = useState(false);
  const [bankRes, setBankRes] = useState({
    pagination: {},
    banks: [],
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    search: "",
    user_code: "",
    role_code: "",
    status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewBank, setViewBank] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    loadBanks();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    if (!isSupplierUser) {
      loadSupplierOptions();
      loadRoleOptions();
    }
  }, [isSupplierUser]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadSupplierOptions = async () => {
    try {
      const res = await suppliersService.list({
        page: 1,
        status: "active",
      });
      setSupplierOptions(
        toArray(res?.resData).map((s) => ({
          value: s.supplier_code,
          label: `${s.agency_name} (${s.supplier_code})`,
        })),
      );
    } catch (err) {
      setSupplierOptions([]);
    }
  };

  const loadRoleOptions = async () => {
    try {
      const res = await rbacRolesService.list({ status: "active" });
      setRoleOptions(
        toArray(res?.resData).map((r) => ({
          value: String(r.code),
          label: `${r.role_type} (${r.code})`,
        })),
      );
    } catch (err) {
      setRoleOptions([]);
    }
  };

  const loadBanks = async () => {
    try {
      setLoading(true);
      start();
      const res = await banksService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
        role_code: Admin_Code, // always Admin_Code (placed after the filters so the Role filter can't override it)
      });
      if (res?.success) {
        setBankRes({
          pagination: res.resData?.pagination || {},
          banks: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleStatusChange = async (bank, newStatus) => {
    // only pending -> verified / rejected is supported by the verify API
    if (newStatus === bank?.status || newStatus === "pending") return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark ${bank?.bank_name} (${bank?.account_number}) as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await banksService.verify(bank?.id, { status: newStatus });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadBanks();
    }
  };

  const handleSetPrimary = async (bank) => {
    const result = await withReactContent(Swal).fire({
      title: "Set as primary?",
      text: `${bank?.bank_name} (${bank?.account_number}) will become the primary account.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, set primary",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await banksService.setPrimary(bank?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadBanks();
    }
  };

  const handleDeleteBank = async (bank) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete bank details?",
      text: `Are you sure you want to delete ${bank?.bank_name} (${bank?.account_number})? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await banksService.delete(bank?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadBanks();
    }
  };

  const handleViewBank = async (bankId) => {
    setShow(true);
    setViewBank(null);
    try {
      setViewLoading(true);
      const res = await banksService.details(bankId);
      if (res?.success) {
        setViewBank(res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ---- Add / Update Bank Details (popup) ----
  const initialForm = {
    account_holder_name: "",
    bank_name: "",
    branch_name: "",
    account_number: "",
    account_type: "savings",
    ifsc_code: "",
    is_primary: false,
  };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  // edit mode: id of the bank being edited (null = add)
  const [editId, setEditId] = useState(null);
  const isEdit = !!editId;

  const resetFormState = () => {
    setForm(initialForm);
  };

  const openAdd = () => {
    setEditId(null);
    resetFormState();
    setShowForm(true);
  };

  const closeForm = () => {
    if (!saving) setShowForm(false);
  };

  const openEdit = async (bankId) => {
    setEditId(bankId);
    resetFormState();
    setShowForm(true);
    try {
      setSaving(true);
      const res = await banksService.details(bankId);
      if (res?.success) {
        const b = res.resData;
        setForm({
          account_holder_name: b?.account_holder_name || "",
          bank_name: b?.bank_name || "",
          branch_name: b?.branch_name || "",
          account_number: b?.account_number || "",
          account_type: b?.account_type || "savings",
          ifsc_code: b?.ifsc_code || "",
          is_primary: !!b?.is_primary,
        });
      } else {
        toast.error(res?.message);
        setShowForm(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: name === "ifsc_code" ? value.toUpperCase() : value,
    });
  };

  const validateForm = () => {
    if (!form.account_holder_name.trim())
      return "Account holder name is required";
    if (!form.bank_name.trim()) return "Bank name is required";
    if (!form.account_number.trim()) return "Account number is required";
    if (!/^\d{6,20}$/.test(form.account_number.trim()))
      return "Enter a valid account number (6 to 20 digits)";
    if (!form.account_type) return "Account type is required";
    if (!form.ifsc_code.trim()) return "IFSC code is required";
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code.trim()))
      return "Enter a valid IFSC code";
    return null;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update bank details?" : "Save bank details?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {
      setSaving(true);

      const payload = {
        // owner of the account comes from the logged-in user
        user_code: user?.code,
        role_code: user?.role_code,
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim(),
        branch_name: form.branch_name.trim(),
        account_number: form.account_number.trim(),
        account_type: form.account_type,
        ifsc_code: form.ifsc_code.trim(),
        is_primary: form.is_primary,
      };
      const res = isEdit
        ? await banksService.update(
            editId,
            (({ user_code, role_code, ...rest }) => rest)(payload),
          )
        : await banksService.create(payload);
      if (res?.success) {
        await withReactContent(Swal).fire({
          title: "Success",
          text: res.message,
          icon: "success",
        });
        setShowForm(false);
        if (isEdit || pageValue === 1) loadBanks();
        else setPageValue(1);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const banks = bankRes?.banks || [];
  const totalRecords = bankRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const verifiedCount = banks.filter((v) => v?.status === "verified").length;
  const pendingCount = banks.filter((v) => v?.status === "pending").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "User Code", key: "user_code" },
    { label: "Account Holder", key: "account_holder_name" },
    { label: "Bank Name", key: "bank_name" },
    { label: "Branch", key: "branch_name" },
    { label: "Account Number", key: "account_number" },
    { label: "Account Type", key: "account_type" },
    { label: "IFSC", key: "ifsc_code" },
    { label: "UPI ID", key: "upi_id" },
    { label: "Primary", key: "is_primary" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 6 };
  const inputStyle = {
    borderRadius: 8,
    border: "1px solid #c3cad6",
    fontSize: 13,
    height: 40,
    background: "#fff",
  };
  const formSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      borderRadius: 8,
      border: state.isFocused ? "1px solid var(--btn-bg)" : "1px solid #c3cad6",
      minHeight: 40,
      fontSize: 13,
      boxShadow: state.isFocused ? "0 0 0 3px rgba(14,106,150,.15)" : "none",
      "&:hover": { borderColor: "var(--btn-bg)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    indicatorsContainer: (base) => ({ ...base, height: 40 }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      background: state.isSelected
        ? "var(--btn-bg)"
        : state.isFocused
          ? "#e6f1f8"
          : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
  };
  const field = (text, required, node, cls = "col-12 col-md-6") => (
    <div className={cls}>
      <div style={labelStyle}>
        {text}
        {required && <span className="text-danger"> *</span>}
      </div>
      {node}
    </div>
  );
  const textInput = (name, placeholder, extra) => (
    <input
      type="text"
      className="form-control"
      name={name}
      placeholder={placeholder}
      value={form[name]}
      onChange={handleFormChange}
      style={inputStyle}
      {...extra}
    />
  );

  const statusBadge = (status) => {
    const c = STATUS_COLORS[status] || { bg: "#eef1f6", color: "#6b7a90" };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          background: c.bg,
          color: c.color,
        }}
      >
        {statusLabel(status)}
      </span>
    );
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Bank Details
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Bank Details</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Bank List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "var(--btn-bg)",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Bank
            </button>
            <CSVLink
              data={banks}
              headers={csvHeaders}
              filename="bank-details.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search bank name, account holder, account number, IFSC"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  style={filterInput}
                />
              </div>
              {/* {!isSupplierUser && (
                <>
                  <div className="col-6 col-md-2">
                    <Select
                      options={supplierOptions}
                      value={
                        supplierOptions.find(
                          (o) => o.value === filters.user_code,
                        ) || null
                      }
                      onChange={(o) =>
                        setFilters({ ...filters, user_code: o ? o.value : "" })
                      }
                      placeholder="Select Supplier"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <Select
                      options={roleOptions}
                      value={
                        roleOptions.find(
                          (o) => o.value === filters.role_code,
                        ) || null
                      }
                      onChange={(o) =>
                        setFilters({ ...filters, role_code: o ? o.value : "" })
                      }
                      placeholder="Select Role"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </div>
                </>
              )} */}
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_OPTIONS}
                  value={
                    STATUS_OPTIONS.find((o) => o.value === filters.status) ||
                    null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, status: o ? o.value : "" })
                  }
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "var(--btn-bg)",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Banks
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{verifiedCount}</b> Verified on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{pendingCount}</b> Pending on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", 
                      "Bank",
                      "Account Holder",
                      "Account",
                      "IFSC",
                      "Owner",
                      "Status",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {banks.map((bank, sno_i) => (
                    <tr
                      key={bank?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 36,
                              height: 36,
                              background: "#e8f0fe",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className="bi bi-bank text-primary"
                              style={{ fontSize: 14 }}
                            ></i>
                          </div>
                          <div>
                            <div
                              className="fw-semibold text-dark"
                              style={{ fontSize: 13 }}
                            >
                              {bank?.bank_name}
                              {bank?.is_primary && (
                                <span
                                  className="ms-2"
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: "2px 8px",
                                    borderRadius: 10,
                                    background: "#e8f0fe",
                                    color: "var(--btn-bg)",
                                  }}
                                >
                                  PRIMARY
                                </span>
                              )}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 11 }}
                            >
                              {bank?.branch_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="fw-medium" style={{ fontSize: 13 }}>
                        {bank?.account_holder_name}
                      </td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {bank?.account_number}
                        </div>
                        <div
                          className="text-muted text-capitalize"
                          style={{ fontSize: 11 }}
                        >
                          {bank?.account_type}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>{bank?.ifsc_code}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {bank?.upi_id || "-"}
                        </div>
                      </td>
                      <td className="fw-medium" style={{ fontSize: 13 }}>
                        {bank?.user_code}
                      </td>
                      <td>
                        {isSupplierUser ? (
                          statusBadge(bank?.status)
                        ) : (
                          <select
                            className="form-select form-select-sm"
                            value={bank?.status}
                            onChange={(e) =>
                              handleStatusChange(bank, e.target.value)
                            }
                            style={{
                              width: "auto",
                              minWidth: 0,
                              height: 28,
                              padding: "0 26px 0 10px",
                              backgroundPosition: "right 8px center",
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              backgroundColor: (
                                STATUS_COLORS[bank?.status] ||
                                STATUS_COLORS.pending
                              ).bg,
                              color: (
                                STATUS_COLORS[bank?.status] ||
                                STATUS_COLORS.pending
                              ).color,
                              border: "none",
                            }}
                          >
                            {STATUS_OPTIONS.map((o) => (
                              <option
                                key={o.value}
                                value={o.value}
                                disabled={o.value === "pending"}
                              >
                                {o.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewBank(bank?.id)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`} data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openEdit(bank?.id)}
                            title="Edit"
                            style={actionBtn("#e8f0fe", "var(--btn-bg)")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.edit}`} data-action="edit"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          {!bank?.is_primary && (
                            <button
                              className="btn btn-sm d-flex align-items-center justify-content-center"
                              onClick={() => handleSetPrimary(bank)}
                              title="Set as primary"
                              style={actionBtn("#fff4e6", "#e8590c")}
                            >
                              <i
                                className={`bi ${ACTION_ICONS.star}`} data-action="star"
                                style={{ fontSize: 13 }}
                              ></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteBank(bank)}
                            title="Delete"
                            style={actionBtn("#fce8e6", "#d93025")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.delete}`} data-action="delete"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && banks.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No bank details found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> banks
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" centered show={showForm} onHide={closeForm}>
          <form onSubmit={handleFormSubmit}>
            <Modal.Header closeButton>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {isEdit ? "Update" : "Add"} Bank Details
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                  {isEdit
                    ? "Update bank account information"
                    : "Add a new bank account"}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                {field(
                  "Account Holder Name",
                  true,
                  textInput("account_holder_name", "Enter account holder name"),
                )}
                {field(
                  "Bank Name",
                  true,
                  textInput("bank_name", "Enter bank name"),
                )}
                {field(
                  "Branch Name",
                  false,
                  textInput("branch_name", "Enter branch name"),
                )}
                {field(
                  "Account Number",
                  true,
                  textInput("account_number", "Enter account number", {
                    inputMode: "numeric",
                  }),
                )}
                {field(
                  "Account Type",
                  true,
                  <Select
                    options={ACCOUNT_TYPE_OPTIONS}
                    value={
                      ACCOUNT_TYPE_OPTIONS.find(
                        (o) => o.value === form.account_type,
                      ) || null
                    }
                    onChange={(o) =>
                      setForm({
                        ...form,
                        account_type: o ? o.value : "savings",
                      })
                    }
                    menuPortalTarget={document.body}
                    styles={formSelectStyles}
                  />,
                )}
                {field(
                  "IFSC Code",
                  true,
                  textInput("ifsc_code", "e.g. HDFC0001234", { maxLength: 11 }),
                )}
                {field(
                  "Primary Account",
                  false,
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_primary"
                      checked={form.is_primary}
                      onChange={(e) =>
                        setForm({ ...form, is_primary: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="is_primary">
                      Use as primary account
                    </label>
                  </div>,
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                className="btn"
                onClick={closeForm}
                disabled={saving}
                style={{
                  background: "#fff",
                  border: "1px solid #c3cad6",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                  color: "#444",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white fw-semibold"
                disabled={saving}
                style={{
                  background: "var(--btn-bg)",
                  border: "none",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                    ? "Update Bank Details"
                    : "Add Bank Details"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Bank Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewLoading || !viewBank ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  { label: "Owner (User Code)", value: viewBank.user_code },
                  { label: "Role Code", value: viewBank.role_code },
                  {
                    label: "Account Holder",
                    value: viewBank.account_holder_name,
                  },
                  { label: "Bank Name", value: viewBank.bank_name },
                  { label: "Branch", value: viewBank.branch_name },
                  { label: "Account Number", value: viewBank.account_number },
                  { label: "Account Type", value: viewBank.account_type },
                  { label: "IFSC Code", value: viewBank.ifsc_code },
                  { label: "MICR Code", value: viewBank.micr_code },
                  { label: "SWIFT Code", value: viewBank.swift_code },
                  { label: "UPI ID", value: viewBank.upi_id },
                  { label: "PAN Number", value: viewBank.pan_number },
                  { label: "Bank Address", value: viewBank.bank_address, full: true },
                  { label: "City", value: viewBank.city },
                  { label: "State", value: viewBank.state },
                  { label: "Pincode", value: viewBank.pincode },
                  {
                    label: "Primary",
                    value: viewBank.is_primary ? "Yes" : "No",
                  },
                  { label: "Status", value: statusLabel(viewBank.status), badge: true },
                  {
                    label: "Verified On",
                    value: viewBank.verification_date
                      ? String(viewBank.verification_date)
                          .replace("T", " ")
                          .slice(0, 19)
                      : null,
                  },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default BankDetails;
